/**
 * OperationHealth — columna lateral: salud de la operación + desglose de
 * pendientes + tarjeta de "SLA en riesgo" (pendiente del back, marcada).
 */
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, Clock, PackageX } from 'lucide-react';
import { Card } from '../../../../components/ui/Card';
import { Skeleton } from '../../../../components/ui/Skeleton';
import { PendingBackendNotice } from '../../../../components/ui/PendingBackendNotice';
import { formatNumberCL } from '../../../../utils/formatters';

function msToHHMM(ms) {
  const minutes = ms > 0 ? Math.round(ms / 60000) : 0;
  return `${Math.floor(minutes / 60)
    .toString()
    .padStart(2, '0')}:${(minutes % 60).toString().padStart(2, '0')}`;
}

function HealthRow({ label, value, pct, tone }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-fg-muted">{label}</span>
        <span className="font-semibold tabular-nums text-fg">{value}</span>
      </div>
      {pct != null && (
        <div className="h-1.5 rounded-full bg-bg-muted overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${Math.min(Math.max(pct, 0), 100)}%`, background: tone }}
          />
        </div>
      )}
    </div>
  );
}

function OperationHealth({ kpis, pendingBreakdown, loading }) {
  const { t } = useTranslation();

  const healthTone =
    kpis.serviceLevelPct >= 90
      ? 'rgb(var(--status-success))'
      : kpis.serviceLevelPct >= 70
        ? 'rgb(var(--status-warning))'
        : 'rgb(var(--status-error))';

  return (
    <div className="space-y-3">
      <Card padding="md">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-8 h-8 rounded-lg bg-status-success-soft text-status-success grid place-items-center">
            <Activity className="w-4 h-4" strokeWidth={2.2} />
          </span>
          <h3 className="text-sm font-semibold text-fg">{t('Salud de la operación')}</h3>
        </div>
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-8 rounded-md" />
            <Skeleton className="h-8 rounded-md" />
            <Skeleton className="h-8 rounded-md" />
          </div>
        ) : (
          <div className="space-y-3">
            <HealthRow
              label={t('Nivel de servicio')}
              value={`${Math.round(kpis.serviceLevelPct)}%`}
              pct={kpis.serviceLevelPct}
              tone={healthTone}
            />
            <HealthRow
              label={t('Cumplimiento de entrega')}
              value={`${Math.round(kpis.compliancePct)}%`}
              pct={kpis.compliancePct}
              tone="rgb(var(--brand-500))"
            />
            <HealthRow
              label={t('Tiempo promedio de entrega')}
              value={msToHHMM(kpis.avgDeliveryMs)}
            />
          </div>
        )}
      </Card>

      <Card padding="md">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-status-warning-soft text-status-warning grid place-items-center">
              <PackageX className="w-4 h-4" strokeWidth={2.2} />
            </span>
            <h3 className="text-sm font-semibold text-fg">{t('Pendientes por gestionar')}</h3>
          </div>
          {!loading && (
            <span className="text-lg font-bold tabular-nums text-fg">
              {formatNumberCL(pendingBreakdown.total)}
            </span>
          )}
        </div>
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-5 rounded-md" />
            <Skeleton className="h-5 rounded-md" />
          </div>
        ) : pendingBreakdown.rows.length === 0 ? (
          <p className="text-xs text-fg-muted py-2">{t('Sin pendientes registrados hoy.')}</p>
        ) : (
          <ul className="space-y-1.5">
            {pendingBreakdown.rows.map((r) => (
              <li key={r.label} className="flex items-center justify-between text-xs">
                <span className="text-fg-muted truncate pr-2">{r.label}</span>
                <span className="font-semibold tabular-nums text-fg shrink-0">
                  {formatNumberCL(r.count)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* SLA en riesgo — el back no expone deadlines por paquete (gap). */}
      <Card padding="md">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-8 h-8 rounded-lg bg-bg-muted text-fg-muted grid place-items-center">
            <Clock className="w-4 h-4" strokeWidth={2.2} />
          </span>
          <h3 className="text-sm font-semibold text-fg">{t('SLA en riesgo')}</h3>
        </div>
        <PendingBackendNotice>
          {t('El riesgo de SLA por paquete está en construcción.')}
        </PendingBackendNotice>
      </Card>
    </div>
  );
}

// memo: props estables entre polls de 90s (referencias del hook).
export default memo(OperationHealth);
